const { default: cityToCountry } = require("./city");

const isAdAccountCampaignReady = (adAccountData) => {
  const {
    account_status,
    disable_reason,
    capabilities,
    currency,
    funding_source_details,
  } = adAccountData;

  const isActive = account_status === 1;
  const isEnabled = disable_reason === 0;

  const requiredCapabilities = [
    "CAN_CREATE_ADS",
    "CAN_CREATE_CALL_ADS",
    "CAN_USE_REACH_AND_FREQUENCY",
  ];
  const hasAdCapabilities = capabilities?.some((cap) =>
    requiredCapabilities.includes(cap)
  );

  const hasCurrency = !!currency;

  // Check if funding_source_details is a valid object with at least one key
  const hasFunding =
    funding_source_details &&
    typeof funding_source_details === "object" &&
    Object.keys(funding_source_details).length > 0;

  // Collect reasons why it's not ready
  const notReadyReasons = [];
  if (!isActive) notReadyReasons.push("Account is not active");
  if (!isEnabled) notReadyReasons.push("Account is disabled");
  if (!hasAdCapabilities)
    notReadyReasons.push("Missing required ad capabilities");
  if (!hasCurrency) notReadyReasons.push("Currency not set");
  if (!hasFunding) notReadyReasons.push("Funding source not available");

  return {
    isReady:
      isActive && isEnabled && hasAdCapabilities && hasCurrency && hasFunding,
    issues: {
      isActive,
      isEnabled,
      hasAdCapabilities,
      hasCurrency,
      hasFunding,
    },
    notReadyReasons, // helpful for UI display
  };
};

const isGoogleCustomerCampaignReady = (customer) => {
  const {
    customerId,
    currencyCode,
    timeZone,
    status
  } = customer;

  const isValidCustomerId = !!customerId;
  const isEnabled = status === 'ENABLED';
  const hasCurrency = !!currencyCode;
  const hasTimeZone = !!timeZone;

  const notReadyReasons = [];
  if (!isValidCustomerId) notReadyReasons.push("Missing customer ID");
  if (!isEnabled) notReadyReasons.push("Customer account is not enabled");
  if (!hasCurrency) notReadyReasons.push("Currency is not set");
  if (!hasTimeZone) notReadyReasons.push("Time zone is not set");

  return {
    isReady: isValidCustomerId && isEnabled && hasCurrency && hasTimeZone,
    issues: {
      isValidCustomerId,
      isEnabled,
      hasCurrency,
      hasTimeZone
    },
    notReadyReasons
  };
};

const getCustomSelectStyles = () => ({
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--primary)',
    border: '0',
    borderRadius: '0',
    color: '#fff',
    fontSize: '14px!important',
    padding: '0',
    margin: '0',
    outline: 'none',
    borderColor: state.isFocused ? 'transparent' : 'transparent',
    boxShadow: state.isFocused ? 'none' : 'none',
  }),
  input: (provided) => ({
    ...provided,
    color: '#fff',
    fontSize: '14px',
    padding: '0',
    margin: '0',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#fff',
    fontSize: '14px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#ff6347' : provided.backgroundColor,
    color: state.isFocused ? 'white' : 'black',
    fontSize: '14px',
  }),
});

const getGoalIconAndName = (campaignName, goalOptions) => {
  if (!campaignName) return null;

  const matchedGoal = goalOptions.find(goal =>
    campaignName.toLowerCase().includes(goal.value.toLowerCase())
  );

  if (!matchedGoal) return null;

  return {
    name: matchedGoal.value,
    icon: matchedGoal.icon
  };
};

const formatFixedAmount = (value) => {
  if (value == null || value === "") return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return amount.toFixed(2);
};

const formatNumber = (num) => {
  if (num >= 1_000_000_000_000) {
    return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "") + "T"; // Trillions
  } else if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"; // Billions
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"; // Millions
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K"; // Thousands
  }
  return num?.toString(); // Numbers below 1,000 remain unchanged
};

// format-date-utils.js

const formatDateToMonthDay = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const monthIndex = parseInt(month, 10) - 1;
  const monthName = monthNames[monthIndex] || month;

  const currentYear = new Date().getFullYear();
  if (parseInt(year, 10) < currentYear) {
    return `${monthName} ${day}, ${year}`;
  }

  return `${monthName} ${day}`;
};

const getCountryInfo = (code) => {
  // Handle various input formats
  const codeStr = String(code || "").trim().toUpperCase();

  // Validate country code
  if (!codeStr || codeStr.length !== 2) {
    return { name: "Unknown", flag: "❓" };
  }

  // Validate that it contains only letters
  if (!/^[A-Z]{2}$/.test(codeStr)) {
    return { name: "Unknown", flag: "❓" };
  }

  try {
    // Convert ISO 3166-1 alpha-2 to flag emoji
    // Regional Indicator Symbol Letter A starts at U+1F1E6 (127462)
    const flag = codeStr
      .split("")
      .map((char) => String.fromCodePoint(127462 + char.charCodeAt(0) - 65))
      .join("");

    // Get country name
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    const name = names.of(codeStr);

    return name ? { name, flag } : { name: "Unknown", flag: "❓" };
  } catch (error) {
    console.error("Error converting country code:", error);
    return { name: "Unknown", flag: "❓" };
  }
};

function getCountryFlagByCity(city) {
  // Convert country code to flag emoji
  function countryCodeToFlagEmoji(countryCode) {
    return countryCode
      ?.toUpperCase()
      ?.replace(/./g, c =>
        String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
      );
  }

  if (typeof city !== "string") return "";

  const countryCode = cityToCountry[city.toLowerCase()];
  return countryCodeToFlagEmoji(countryCode);
}

const filterPlatformData = (data) => {
  const allowedSources = [
    "spotify",
    "apple_music",
    "amazon",
    "itunes",
    "soundcloud",
    "youtube",
    "instagram",
    "tiktok",
  ];

  return data.filter((item) => allowedSources.includes(item.source));
};

function getArtistNames(artists) {
  if (!Array.isArray(artists)) return "";
  return artists.map((artist) => artist.name).join(", ");
}

function getFileExtensionRegex(url) {
  const match = url?.match(/\.([^./?#]+)(?:[?#]|$)/);
  return match ? match[1].toLowerCase() : '';
}

function formatDuration(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

function getAudioDuration(audioUrl) {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    
    audio.addEventListener('loadedmetadata', () => {
      // Check if duration is valid
      if (audio.duration === Infinity || isNaN(audio.duration)) {
        // Handle edge case for some audio formats (like streaming)
        audio.currentTime = 1e101; // Set to very high value to force duration calculation
        
        audio.addEventListener('timeupdate', () => {
          audio.currentTime = 0;
          const duration = audio.duration;
          resolve({
            duration: duration,
            formatted: formatDuration(duration)
          });
        }, { once: true });
      } else {
        const duration = audio.duration;
        resolve({
          duration: duration,
          formatted: formatDuration(duration)
        });
      }
    });

    audio.addEventListener('error', (e) => {
      reject(new Error(`Failed to load audio: ${audio.error?.message || 'Unknown error'}`));
    });

    // Set CORS for cross-origin requests
    audio.crossOrigin = 'anonymous';
    
    // Load the audio
    audio.src = audioUrl;
    audio.load();
  });
}

const getYoutubeVideoId = (url) => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace("www.", "");

    if (hostname === "youtu.be") {
      return parsedUrl.pathname.split("/").filter(Boolean)[0] || null;
    }

    if (hostname.endsWith("youtube.com")) {
      const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
      return parsedUrl.searchParams.get("v") ||
        (["embed", "shorts", "live"].includes(pathParts[0]) ? pathParts[1] : null);
    }
  } catch {
    return null;
  }

  return null;
};

const getYoutubeThumbnail = (url) => {
  const videoId = getYoutubeVideoId(url);

  if (!videoId) return "";

  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

/** Format a campaign budget using ad-account / stored currency code (e.g. INR 500). */
const formatCampaignMoney = (amount, currency) => {
  const value = Number(amount);
  const display = Number.isFinite(value) ? value.toLocaleString() : String(amount ?? "");
  if (currency) return `${currency} ${display}`;
  return `$${display}`;
};

const moment = require("moment-timezone");

/** Friendly timezone label, e.g. "IST (Asia/Kolkata)" */
const getTimezoneLabel = (timezone) => {
  if (!timezone) return "";
  try {
    const abbr = moment.tz(timezone).format("z");
    if (abbr && abbr !== timezone) return `${abbr} (${timezone})`;
  } catch (_) { /* ignore */ }
  return timezone;
};

/**
 * Format a stored UTC instant in the campaign/ad-account timezone.
 * @param {string|Date} dateInput
 * @param {string} timezone IANA zone e.g. Asia/Kolkata
 * @param {{ dateOnly?: boolean, timeOnly?: boolean, includeTimezone?: boolean }} options
 */
const formatCampaignDateTime = (dateInput, timezone, options = {}) => {
  if (!dateInput) return "";
  const { dateOnly = false, timeOnly = false, includeTimezone = false } = options;
  let m;
  try {
    m = timezone ? moment.tz(dateInput, timezone) : moment(dateInput);
  } catch (_) {
    m = moment(dateInput);
  }
  if (!m.isValid()) return "";

  let formatted;
  if (dateOnly) {
    formatted = m.format("MMM D, YYYY");
  } else if (timeOnly) {
    formatted = m.format("h:mm A");
  } else {
    formatted = m.format("MMM D, YYYY, h:mm A");
  }

  if (includeTimezone && timezone) {
    return `${formatted} · ${getTimezoneLabel(timezone)}`;
  }
  return formatted;
};

/**
 * Calendar date YYYY-MM-DD for the date picker value (local calendar day of the Date object).
 */
const toScheduleDateString = (dateInput) => {
  if (!dateInput) return "";
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Calendar date YYYY-MM-DD of an instant in the Meta ad account timezone.
 * Use for stored UTC/ISO times; picker cells should keep using toScheduleDateString.
 */
const toAccountScheduleDateString = (dateInput, timezone) => {
  if (!dateInput) return "";
  try {
    if (timezone) {
      const m = moment.tz(dateInput, timezone);
      if (m.isValid()) return m.format("YYYY-MM-DD");
    }
  } catch (_) { /* ignore */ }
  return toScheduleDateString(dateInput);
};

/**
 * Today's calendar date (YYYY-MM-DD) in the Meta ad account timezone.
 * Falls back to the browser timezone if none is provided.
 */
const getAccountTodayString = (timezone) => {
  try {
    if (timezone) return moment.tz(timezone).format("YYYY-MM-DD");
  } catch (_) { /* ignore */ }
  return moment().format("YYYY-MM-DD");
};

/**
 * Build a Date at local noon for a YYYY-MM-DD calendar day (stable for pickers).
 */
const calendarDateFromString = (yyyyMmDd) => {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return null;
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

/** True if the picker cell's calendar day is "today" in the account timezone. */
const isAccountCalendarToday = (dateInput, timezone) => {
  const day = toScheduleDateString(dateInput);
  return !!day && day === getAccountTodayString(timezone);
};

/**
 * True if the picker cell's calendar day is before account-today.
 * (Account-today itself is not "before".)
 */
const isBeforeAccountToday = (dateInput, timezone) => {
  const day = toScheduleDateString(dateInput);
  if (!day) return false;
  return day < getAccountTodayString(timezone);
};

/** Minutes ahead of account "now" required for start time on account-today. */
const START_TIME_BUFFER_MINUTES = 30;

/** Current HH:mm in the Meta ad account timezone. */
const getAccountNowTimeString = (timezone) => {
  try {
    if (timezone) return moment.tz(timezone).format("HH:mm");
  } catch (_) { /* ignore */ }
  return moment().format("HH:mm");
};

/** Current time in 12-hour format for the Meta ad account timezone. */
const getAccountNowDisplay12h = (timezone) => {
  try {
    const m = timezone ? moment.tz(timezone) : moment();
    return m.format("h:mm A");
  } catch (_) {
    return moment().format("h:mm A");
  }
};

/** Format HH:mm schedule value as 12-hour time, e.g. "2:30 PM". */
const formatScheduleTime12h = (timeHHMM) => {
  if (!timeHHMM) return "";
  const m = moment(timeHHMM, ["HH:mm", "H:mm"], true);
  return m.isValid() ? m.format("h:mm A") : timeHHMM;
};

/**
 * Earliest allowed start time (HH:mm) for a selected calendar day.
 * Returns null when any time is allowed (future day) or date is missing.
 * On account-today, returns account now + START_TIME_BUFFER_MINUTES.
 */
const getMinStartTimeForDate = (dateInput, timezone, bufferMinutes = START_TIME_BUFFER_MINUTES) => {
  const day = toScheduleDateString(dateInput);
  if (!day) return null;
  const today = getAccountTodayString(timezone);
  if (day !== today) return null;
  try {
    const m = timezone ? moment.tz(timezone) : moment();
    m.add(bufferMinutes, "minutes").startOf("minute");
    return m.format("HH:mm");
  } catch (_) {
    const fallback = moment(getAccountNowTimeString(timezone), "HH:mm").add(bufferMinutes, "minutes");
    return fallback.format("HH:mm");
  }
};

/**
 * True when start date+time is before the earliest allowed slot (account-today buffer).
 */
const isScheduleStartTimeTooSoon = (dateInput, timeHHMM, timezone, bufferMinutes = START_TIME_BUFFER_MINUTES) => {
  const minTime = getMinStartTimeForDate(dateInput, timezone, bufferMinutes);
  if (!minTime) return false;
  if (!timeHHMM) return true;
  return timeHHMM < minTime;
};

/** @deprecated use isScheduleStartTimeTooSoon */
const isScheduleDateTimeInPast = (dateInput, timeHHMM, timezone) =>
  isScheduleStartTimeTooSoon(dateInput, timeHHMM, timezone);

/**
 * Meta-aligned min budget:
 * - daily → min daily (high freq)
 * - lifetime → min daily × campaign days
 *
 * @param {object} options
 * @param {boolean} [options.useRemainingDays] If true (edit flow), count from max(now, start) → end
 * @param {number} [options.spentAmount] Lifetime floor cannot go below amount already spent
 * @param {string} [options.timezone] Used with useRemainingDays for "now" in account TZ
 */
const getCampaignMinBudget = ({
  budgetType = "daily",
  minDailyBudget = 0,
  startDate,
  startTime = "00:00",
  endDate,
  endTime = "23:59",
  useRemainingDays = false,
  spentAmount = 0,
  timezone = "",
}) => {
  const minDaily = Number(minDailyBudget) || 0;
  const spentFloor = Math.max(0, Math.ceil(Number(spentAmount) || 0));
  if (!minDaily) return budgetType === "lifetime" ? spentFloor : 0;
  if (budgetType !== "lifetime") return minDaily;
  if (!startDate || !endDate) return Math.max(minDaily, spentFloor);

  let start = new Date(startDate);
  let end = new Date(endDate);
  if (startTime) {
    const [h, m] = String(startTime).split(":").map(Number);
    start.setHours(h || 0, m || 0, 0, 0);
  }
  if (endTime) {
    const [h, m] = String(endTime).split(":").map(Number);
    end.setHours(h || 0, m || 0, 0, 0);
  }

  if (useRemainingDays) {
    let now;
    try {
      now = timezone ? moment.tz(timezone).toDate() : new Date();
    } catch (_) {
      now = new Date();
    }
    if (start.getTime() < now.getTime()) {
      start = now;
    }
  }

  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const scheduleMin = minDaily * Math.max(days, 1);
  return Math.max(scheduleMin, spentFloor);
};

module.exports = {
  isAdAccountCampaignReady,
  isGoogleCustomerCampaignReady,
  getCustomSelectStyles,
  getFileExtensionRegex,
  getGoalIconAndName,
  formatNumber,
  formatFixedAmount,
  formatDateToMonthDay,
  getCountryInfo,
  filterPlatformData,
  getArtistNames,
  getCountryFlagByCity,
  getAudioDuration,
  getYoutubeVideoId,
  getYoutubeThumbnail,
  formatCampaignMoney,
  getCampaignMinBudget,
  getTimezoneLabel,
  formatCampaignDateTime,
  toScheduleDateString,
  toAccountScheduleDateString,
  getAccountTodayString,
  calendarDateFromString,
  isAccountCalendarToday,
  isBeforeAccountToday,
  getAccountNowTimeString,
  getAccountNowDisplay12h,
  formatScheduleTime12h,
  getMinStartTimeForDate,
  isScheduleStartTimeTooSoon,
  isScheduleDateTimeInPast,
  START_TIME_BUFFER_MINUTES,
};
